import { useQuery } from '@apollo/client';
import React, { useState } from 'react';
import getRedirectQueryString from '../account/getRedirectQueryString';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import IconButton from '../components/IconButton';
import SVGIcon from '../components/SVGIcon';
import { PostLinkAccess, UserType } from '../graphql/generated/graphql';
import searchIcon from '../icons/search.svg';
import TeamIcon from '../organization/TeamIcon';
import Sizes from '../utils/responsive/Sizes';
import { useIsSize } from '../utils/responsive/isSize';
import useSlug from '../utils/router/useSlug';
import ExternalSearchInput from './ExternalSearchInput';
import styles from './ExternalTopbar.module.scss';
import PostModeToggle from './PostModeToggle';
import { gql } from '../graphql/generated/gql';

const POST_QUERY = gql(`
  query ExternalTopbarPost($id: ID!) {
    publicPost(id: $id) {
      id
      linkAccess
    }
  }
`);

interface BaseExternalTopbarProps {
  children: React.ReactNode;
}
const BaseExternalTopbar = ({ children }: BaseExternalTopbarProps) => {
  return (
    <header id="topbar" className={styles.externalTopbar} role="banner">
      {children}
    </header>
  );
};

const LoginButton = () => {
  const redirectPath = useAppSelector(
    (state) => state.ui.externalTopbarLoginRedirectPath,
  );

  return (
    <Button
      variant="secondary"
      to={`/login${getRedirectQueryString(redirectPath)}`}
    >
      Log in
    </Button>
  );
};

const TeamIconLink = () => {
  return (
    <a href="/">
      <TeamIcon />
    </a>
  );
};

const PostToggleButton = () => {
  const [postId] = useSlug('postSlug');
  const currentUser = useAppSelector((state) => state.session.user);
  const readOnly = useAppSelector((state) => state.editor.readOnly);

  const { data: postData } = useQuery(POST_QUERY, {
    variables: { id: postId || '' },
    skip: !postId,
  });

  if (postData?.publicPost.linkAccess !== PostLinkAccess.PublicEdit) {
    return null;
  }

  return (
    <>
      {!readOnly && currentUser.type === UserType.Visitor && (
        <div className={styles.editingNote}>
          You are editing as {currentUser.name}
        </div>
      )}
      <PostModeToggle />
      <div className={styles.modeToggleSeparator} />
    </>
  );
};

const ExternalTopbarWithSearch = () => {
  const [isShowingSearchOnMobile, setIsShowingSearchOnMobile] = useState(false);
  const isSmall = useIsSize(Sizes.SM);

  return (
    <BaseExternalTopbar>
      <div className={styles.left}>
        <TeamIconLink />
        {(!isSmall || isShowingSearchOnMobile) && (
          <ExternalSearchInput autoFocus={isSmall} />
        )}
        {!isShowingSearchOnMobile && isSmall && (
          <IconButton
            onClick={() => setIsShowingSearchOnMobile(true)}
            aria-label="Toggle search input"
          >
            <SVGIcon size="large">{searchIcon}</SVGIcon>
          </IconButton>
        )}

        {isSmall && isShowingSearchOnMobile && (
          <Button onClick={() => setIsShowingSearchOnMobile(false)}>
            Cancel
          </Button>
        )}
      </div>

      <div className={styles.actions}>
        <PostToggleButton />
        {(!isShowingSearchOnMobile || !isSmall) && <LoginButton />}
      </div>
    </BaseExternalTopbar>
  );
};

const ExternalTopbar = () => {
  return (
    <BaseExternalTopbar>
      <TeamIconLink />
      <div className={styles.actions}>
        <PostToggleButton />
        <LoginButton />
      </div>
    </BaseExternalTopbar>
  );
};

ExternalTopbar.WithSearch = ExternalTopbarWithSearch;

export default ExternalTopbar;
